import { Client, StompSubscription, TickerStrategy } from '@stomp/stompjs';
import { batch } from 'react-redux';
import { Dispatch } from 'redux';
import {
  Actions as StreamApiActions,
  CommandType,
} from '../../actions/streamApi';
import {
  CHANNEL_CONFIG,
  ChannelConfig,
  WEBSOCKET_ENDPOINTS,
} from '../../config/websocketChannels';
import {
  createDefaultStompClock,
  exceedsUtf8Limit,
  nextReconnectDelay,
  payloadSizeBucket,
  STOMP_CONFIG,
  utf8ByteLength,
} from './config';
import { InboundDispatcher, InboundWorkItem } from './inboundDispatcher';
import { getStompMetricsSink, hasStompMetricsSink } from './metrics';
import { BoundedOutboundQueue, OutboundDrop } from './outboundQueue';
import {
  ConnectionState,
  DropReason,
  LogicalSubscription,
  PublishResult,
  QueuedOutboundMessage,
  StompClock,
} from './types';

export type StreamStore = {
  getState: () => any;
  dispatch: Dispatch;
};

export type StompClientConstructor = new (config?: any) => Client;

export type StompConnectionManagerOptions = {
  ClientClass?: StompClientConstructor;
  clock?: StompClock;
  random?: () => number;
  brokerURL?: string | undefined;
  /** Collapses the store notifications of one inbound batch into a single render. */
  batchDispatch?: (fn: () => void) => void;
  /** Set false to skip `online` / `visibilitychange` wake-ups (tests). */
  bindEnvironmentListeners?: boolean;
};

export type StompConnectOptions = {
  force?: boolean;
};

/**
 * Owns the single STOMP session for a browser session: one client, one reconnect
 * loop, one logical subscription registry, one outbound queue.
 *
 * Every STOMP callback, timer and queue flush is tagged with the connection
 * generation it was created for, so a client that is being torn down can never
 * dispatch into the store or resurrect a subscription.
 */
export class StompConnectionManager {
  private ClientClass: StompClientConstructor;
  private clock: StompClock;
  private random: () => number;
  private brokerURL: string | undefined;
  private batchDispatch: (fn: () => void) => void;
  private shouldBindEnvironmentListeners: boolean;

  private client: Client | null = null;
  private store: StreamStore | null = null;
  private accessToken: string | null = null;
  private generation = 0;
  private restoredForGeneration = -1;
  private intentionalDisconnect = false;
  private connectedDispatched = false;
  private reconnectAttempts = 0;
  private reconnectTimer: any = null;
  private flushTimer: any = null;
  private restoreTimer: any = null;
  private restoreQueue: string[] = [];
  private restoreCursor = 0;
  private listenersBound = false;
  private state: ConnectionState = 'disconnected';
  private stateChangedAt = 0;

  private registry = new Map<string, LogicalSubscription>();
  private totalRefs = 0;
  private outbound: BoundedOutboundQueue;
  private inbound: InboundDispatcher;

  constructor(options: StompConnectionManagerOptions = {}) {
    this.ClientClass = options.ClientClass || Client;
    this.clock = options.clock || createDefaultStompClock();
    this.random = options.random || Math.random;
    this.brokerURL = options.brokerURL || WEBSOCKET_ENDPOINTS.STOMP_SERVICE;
    this.batchDispatch = options.batchDispatch || batch;
    this.shouldBindEnvironmentListeners =
      options.bindEnvironmentListeners !== false;
    this.stateChangedAt = this.clock.now();
    this.outbound = new BoundedOutboundQueue();
    this.inbound = new InboundDispatcher({
      clock: this.clock,
      onFlush: items => this.applyInboundBatch(items),
      onCoalesced: (channel, droppedCount) => {
        if (hasStompMetricsSink()) {
          getStompMetricsSink().onCoalesced?.({ channel, droppedCount });
        }
      },
    });
  }

  getGeneration() {
    return this.generation;
  }

  getState() {
    return this.state;
  }

  getLogicalSubscriptionCount() {
    return this.registry.size;
  }

  getSubscriptionRefCount(key: string) {
    return this.registry.get(key)?.refCount || 0;
  }

  getQueueSnapshot() {
    return this.outbound.snapshot();
  }

  isIntentionalDisconnect() {
    return this.intentionalDisconnect;
  }

  hasActiveSession() {
    return (
      !this.intentionalDisconnect &&
      (this.state === 'connecting' ||
        this.state === 'reconnecting' ||
        this.isConnected() ||
        this.reconnectTimer != null)
    );
  }

  connect(
    store: StreamStore,
    accessToken?: string | null,
    options: StompConnectOptions = {}
  ) {
    const nextToken = accessToken || this.accessToken;
    if (!nextToken) {
      return;
    }

    this.store = store;
    if (
      !options.force &&
      nextToken === this.accessToken &&
      this.hasActiveSession()
    ) {
      return;
    }

    this.accessToken = nextToken;
    this.intentionalDisconnect = false;
    this.reconnectAttempts = 0;
    this.bindEnvironmentListeners();
    this.startClient();
  }

  disconnect() {
    if (
      this.intentionalDisconnect &&
      !this.client &&
      this.reconnectTimer == null &&
      this.flushTimer == null &&
      this.registry.size === 0 &&
      this.outbound.count === 0
    ) {
      return;
    }

    this.intentionalDisconnect = true;
    this.setState('disconnecting');
    this.unbindEnvironmentListeners();
    this.invalidateGeneration();
    this.dropClient();

    this.registry.clear();
    this.totalRefs = 0;

    this.recordDrops(this.outbound.clear(), 'enqueue');
    this.emitQueueSize();
    this.emitLogicalCount();
    this.dispatchClosed('Client deactivated');

    this.setState('disconnected');
    getStompMetricsSink().onCleanupComplete?.({
      generation: this.generation,
    });
    this.accessToken = null;
    this.store = null;
  }

  /**
   * Registers interest in a topic. Several mounted components routinely watch
   * the same device, so interest is reference counted: the STOMP subscription
   * is created on the first caller and torn down only by the last one.
   */
  subscribe(store: StreamStore, data: any) {
    this.store = store;
    const config = this.getChannelConfig(data);
    if (!config || !config.validateData(data)) {
      return;
    }

    const key = config.getSubscriptionKey(data);
    const existing = this.registry.get(key);

    if (existing) {
      existing.refCount += 1;
      existing.data = data;
      this.totalRefs += 1;
      this.emitLogicalCount();
      if (existing.ref) {
        this.recordDuplicateSubscription(key, data.channel);
        return;
      }
    } else {
      this.registry.set(key, { data, ref: null, refCount: 1 });
      this.totalRefs += 1;
      this.emitLogicalCount();
    }

    if (this.isConnected()) {
      this.activateSubscription(key, config);
    }
  }

  unsubscribe(store: StreamStore, data: any) {
    this.store = store;
    const config = this.getChannelConfig(data);
    if (!config) {
      return;
    }

    const key = config.getSubscriptionKey(data);
    const existing = this.registry.get(key);
    if (!existing) {
      return;
    }

    if (existing.refCount > 1) {
      existing.refCount -= 1;
      this.totalRefs -= 1;
      this.emitLogicalCount();
      return;
    }

    this.releaseSubscription(key);
  }

  publishEnqueue(
    store: StreamStore,
    command: CommandType | string,
    data: any
  ): PublishResult {
    if (this.intentionalDisconnect) {
      this.recordDrop('inactive', String(command));
      return { status: 'dropped', reason: 'inactive' };
    }
    this.store = store;
    const token = this.getAccessToken();
    if (!token) {
      this.recordDrop('no_token', String(command));
      return { status: 'dropped', reason: 'no_token' };
    }

    let body: string;
    try {
      body = JSON.stringify(data);
    } catch (error) {
      console.error('Error serializing enqueue message:', error);
      this.recordDrop('too_large', String(command));
      return { status: 'dropped', reason: 'too_large' };
    }

    const bytes = utf8ByteLength(body);
    if (hasStompMetricsSink()) {
      getStompMetricsSink().onPayloadSize?.({
        direction: 'out',
        bucket: payloadSizeBucket(bytes),
        bytes,
      });
    }

    if (bytes > STOMP_CONFIG.maxPayloadBytes) {
      this.recordDrop('too_large', String(command), bytes);
      return { status: 'dropped', reason: 'too_large' };
    }

    const message: QueuedOutboundMessage = {
      command: String(command),
      destination: `/app/${command}`,
      body,
      bytes,
      enqueuedAt: this.clock.now(),
    };

    const canSendImmediately =
      this.isConnected() &&
      this.outbound.count === 0 &&
      this.flushTimer == null;
    if (canSendImmediately && this.sendOutbound(message)) {
      return { status: 'sent' };
    }

    const drops = this.outbound.enqueue(message, this.clock.now());
    this.recordDrops(drops, 'enqueue');
    this.emitQueueSize();
    if (this.isConnected() && this.flushTimer == null) {
      const gen = this.generation;
      this.flushTimer = this.clock.setTimeout(() => {
        this.runFlushBatch(gen);
      }, 0);
    }
    return { status: 'queued' };
  }

  private getChannelConfig(data: any): ChannelConfig | undefined {
    const config = CHANNEL_CONFIG[data?.channel as keyof typeof CHANNEL_CONFIG];
    if (!config) {
      console.warn(`Unknown STOMP channel: ${data?.channel}`);
      return undefined;
    }
    return config;
  }

  private startClient() {
    // Anything already buffered arrived on the connection we are replacing and
    // still belongs in the store.
    this.inbound.flush();
    this.invalidateGeneration();
    this.dropClient();

    const gen = this.generation;
    this.setState(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');

    const client = new this.ClientClass({
      brokerURL: this.brokerURL,
      connectHeaders: {
        Authorization: `Bearer ${this.accessToken || ''}`,
      },
      // The library has its own backoff, but its retries are untagged: they can
      // outlive a generation and resurrect a session being torn down. This class
      // owns the loop so every attempt carries a generation, and so that
      // `online` / `visibilitychange` can collapse the wait.
      reconnectDelay: 0,
      connectionTimeout: STOMP_CONFIG.connectionTimeoutMs,
      discardWebsocketOnCommFailure: true,
      heartbeatIncoming: STOMP_CONFIG.heartbeatIncomingMs,
      heartbeatOutgoing: STOMP_CONFIG.heartbeatOutgoingMs,
      debug: process.env.NODE_ENV === 'development' ? console.log : () => {},
    });

    client.heartbeatStrategy = TickerStrategy.Worker;

    client.onConnect = () => {
      this.handleConnect(gen);
    };

    client.onStompError = frame => {
      if (gen !== this.generation) {
        return;
      }
      console.error('STOMP error:', frame);
      this.store?.dispatch({
        type: StreamApiActions.ERROR,
        error: frame,
      });
    };

    client.onWebSocketError = error => {
      if (gen !== this.generation) {
        return;
      }
      console.error('STOMP WebSocket error:', error);
      this.store?.dispatch({
        type: StreamApiActions.ERROR,
        error,
      });
    };

    client.onWebSocketClose = (event: any) => {
      this.handleWebSocketClose(event, gen);
    };

    this.client = client;
    client.activate();
  }

  private handleConnect(gen: number) {
    if (gen !== this.generation) {
      return;
    }

    if (this.reconnectAttempts > 0) {
      getStompMetricsSink().onReconnectOutcome?.({
        attempt: this.reconnectAttempts,
        outcome: 'success',
      });
    }
    this.reconnectAttempts = 0;
    this.setState('connected');
    this.dispatchConnected();

    // The outbound flush is started by the restore, not here: a queued
    // ENQUEUE_* command is answered on the topic its device is subscribed to,
    // so publishing it before that SUBSCRIBE frame is sent loses the reply.
    this.restoreSubscriptions(gen);
  }

  private handleWebSocketClose(event: any, gen: number) {
    if (gen !== this.generation) {
      return;
    }

    this.inbound.flush();
    this.clearFlushTimer();
    this.clearRestoreTimer();
    this.nullLiveRefs();

    const closeReason = event?.reason || 'Unknown reason';
    const closeCode = event?.code == null ? '' : ` ${event.code}`;
    this.dispatchClosed(`WebSocket closed: ${closeReason}${closeCode}`);

    if (this.intentionalDisconnect) {
      this.setState('disconnected');
      return;
    }

    this.scheduleReconnect(gen);
  }

  private scheduleReconnect(gen: number) {
    if (gen !== this.generation || this.intentionalDisconnect) {
      return;
    }

    this.clearReconnectTimer();
    const delay = nextReconnectDelay(this.reconnectAttempts, this.random);
    this.setState('reconnecting');
    getStompMetricsSink().onReconnectAttempt?.({
      attempt: this.reconnectAttempts + 1,
      delayMs: delay,
    });

    this.reconnectTimer = this.clock.setTimeout(() => {
      this.reconnectTimer = null;
      if (gen !== this.generation || this.intentionalDisconnect) {
        return;
      }
      this.reconnectAttempts += 1;
      this.startClient();
    }, delay);
  }

  /**
   * Restores the registry over several turns. A site view can hold hundreds of
   * topics and each one costs a SUBSCRIBE frame plus an `/app/subscribe`
   * publish, which must not land in a single task on the connect callback.
   */
  private restoreSubscriptions(gen: number) {
    if (gen !== this.generation || this.restoredForGeneration === gen) {
      return;
    }
    this.restoredForGeneration = gen;
    this.restoreQueue = Array.from(this.registry.keys());
    this.restoreCursor = 0;
    this.runRestoreBatch(gen);
  }

  private runRestoreBatch(gen: number) {
    this.restoreTimer = null;
    if (gen !== this.generation || !this.isConnected()) {
      this.clearRestoreQueue();
      return;
    }

    // A cursor rather than re-slicing: the queue holds every topic in the
    // registry, so copying the remainder each turn would be quadratic.
    const end = Math.min(
      this.restoreQueue.length,
      this.restoreCursor + STOMP_CONFIG.restoreBatchSize
    );
    for (let i = this.restoreCursor; i < end; i++) {
      const key = this.restoreQueue[i];
      const entry = this.registry.get(key);
      const config = entry && this.getChannelConfig(entry.data);
      if (config) {
        this.activateSubscription(key, config);
      }
    }
    this.restoreCursor = end;

    if (this.restoreCursor < this.restoreQueue.length) {
      this.restoreTimer = this.clock.setTimeout(() => {
        this.runRestoreBatch(gen);
      }, 0);
      return;
    }
    this.clearRestoreQueue();
    // Every topic is subscribed again, so queued commands can safely go out.
    this.runFlushBatch(gen);
  }

  private clearRestoreQueue() {
    this.restoreQueue = [];
    this.restoreCursor = 0;
  }

  private activateSubscription(key: string, config: ChannelConfig) {
    const entry = this.registry.get(key);
    if (!entry || !this.client || !this.client.connected) {
      return;
    }
    if (entry.ref) {
      this.recordDuplicateSubscription(key, entry.data.channel);
      return;
    }

    try {
      const gen = this.generation;
      const subscription: StompSubscription = this.client.subscribe(
        config.getTopic(entry.data),
        message => this.handleChannelMessage(message, key, config, gen),
        {
          Authorization: `Bearer ${this.accessToken}`,
        }
      );

      entry.ref = subscription;
      this.publishSubscribeMessage(entry.data);
    } catch (error) {
      console.error(
        `Error subscribing to ${entry.data.channel} via STOMP:`,
        error
      );
    }
  }

  /** Drops a topic outright, whatever its reference count. */
  private releaseSubscription(key: string) {
    const entry = this.registry.get(key);
    if (!entry) {
      return;
    }
    this.totalRefs = Math.max(0, this.totalRefs - entry.refCount);
    this.registry.delete(key);
    if (entry.ref) {
      try {
        entry.ref.unsubscribe();
      } catch (error) {
        // The subscription may already be dead after a socket close.
      }
    }
    this.emitLogicalCount();
  }

  private handleChannelMessage(
    message: any,
    key: string,
    config: ChannelConfig,
    gen: number
  ) {
    if (gen !== this.generation || this.intentionalDisconnect) {
      return;
    }

    const entry = this.registry.get(key);
    if (!entry) {
      return;
    }

    const body = message?.body || '';
    const channel = entry.data.channel || 'unknown';

    if (exceedsUtf8Limit(body, STOMP_CONFIG.maxPayloadBytes)) {
      this.recordDrop('too_large', channel, utf8ByteLength(body));
      return;
    }

    let bytes: number | undefined;
    if (hasStompMetricsSink()) {
      bytes = utf8ByteLength(body);
      getStompMetricsSink().onPayloadSize?.({
        direction: 'in',
        bucket: payloadSizeBucket(bytes),
        bytes,
      });
    }

    let parsed: any;
    try {
      parsed = JSON.parse(body);
    } catch (error) {
      console.error(`Error parsing STOMP ${channel} message:`, error);
      return;
    }

    this.inbound.submit({
      key,
      channel,
      coalesceKey: config.inboundPolicy === 'latest' ? key : null,
      parsed,
      originalData: entry.data,
      config,
      bytes,
      coalescedCount: 0,
    });
  }

  private applyInboundBatch(items: InboundWorkItem[]) {
    const store = this.store;
    if (!store || this.intentionalDisconnect) {
      return;
    }

    const measure = hasStompMetricsSink();
    const startedAt = measure ? this.now() : 0;

    // One store notification pass for the whole batch instead of one per
    // message: a burst over a hundred devices costs a single render.
    this.batchDispatch(() => {
      for (let i = 0; i < items.length; i++) {
        this.applyInbound(store, items[i], measure);
      }
    });

    if (measure) {
      getStompMetricsSink().onInboundFlush?.({
        items: items.length,
        durationMs: this.now() - startedAt,
      });
    }
  }

  private applyInbound(
    store: StreamStore,
    item: InboundWorkItem,
    measure: boolean
  ) {
    const startedAt = measure ? this.now() : 0;

    try {
      // Read per item, not once per batch: `batch` collapses the notification
      // pass, not the reducers, so an earlier item in this batch has already
      // changed the state that a later one may need to read.
      const action = item.config.processMessage(
        item.parsed,
        item.originalData,
        store.getState()
      );

      if (item.config.shouldAutoUnsubscribe?.(item.parsed)) {
        this.releaseSubscription(item.key);
      }

      store.dispatch(action);
    } catch (error) {
      console.error(`Error applying STOMP ${item.channel} message:`, error);
      return;
    }

    if (!measure) {
      return;
    }
    getStompMetricsSink().onMessageIn?.({
      channel: item.channel,
      bytes: item.bytes,
    });
    const durationMs = this.now() - startedAt;
    if (durationMs >= STOMP_CONFIG.inboundSlowHandlerMs) {
      getStompMetricsSink().onSlowHandler?.({
        channel: item.channel,
        durationMs,
      });
    }
  }

  private publishSubscribeMessage(data: any) {
    let body: string;
    try {
      body = JSON.stringify(data);
    } catch (error) {
      console.error('Error serializing subscribe message:', error);
      return;
    }
    if (exceedsUtf8Limit(body, STOMP_CONFIG.maxPayloadBytes)) {
      this.recordDrop('too_large', 'subscribe', utf8ByteLength(body));
      return;
    }
    this.publish('/app/subscribe', body, 'subscribe');
  }

  /**
   * The single point where a frame leaves this class: one place that reads the
   * token, attaches the auth header and reports the send.
   */
  private publish(
    destination: string,
    body: string,
    category: string,
    bytes?: number
  ): boolean {
    const token = this.getAccessToken();
    if (!this.client || !token) {
      return false;
    }
    try {
      this.client.publish({
        destination,
        body,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (publishError) {
      console.error(`Error publishing ${category} message:`, publishError);
      return false;
    }
    if (hasStompMetricsSink()) {
      getStompMetricsSink().onMessageOut?.({
        destination,
        category,
        bytes: bytes ?? utf8ByteLength(body),
      });
    }
    return true;
  }

  private runFlushBatch(gen: number) {
    this.clearFlushTimer();
    if (gen !== this.generation || !this.isConnected() || !this.client) {
      return;
    }

    this.recordDrops(this.outbound.expire(this.clock.now()), 'enqueue');

    const batchToSend = this.outbound.drain(
      STOMP_CONFIG.outboundFlushBatchSize
    );
    for (let i = 0; i < batchToSend.length; i++) {
      if (
        gen !== this.generation ||
        !this.isConnected() ||
        !this.client ||
        !this.sendOutbound(batchToSend[i])
      ) {
        if (!this.intentionalDisconnect) {
          this.outbound.prepend(batchToSend.slice(i));
        }
        this.emitQueueSize();
        return;
      }
    }

    this.emitQueueSize();

    if (this.outbound.count > 0 && this.isConnected()) {
      this.flushTimer = this.clock.setTimeout(() => {
        this.runFlushBatch(gen);
      }, 0);
    }
  }

  private sendOutbound(message: QueuedOutboundMessage): boolean {
    return this.publish(
      message.destination,
      message.body,
      message.command,
      message.bytes
    );
  }

  private isConnected() {
    return !!this.client && !!this.client.connected;
  }

  private now() {
    return typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : this.clock.now();
  }

  private getAccessToken() {
    return this.accessToken || this.store?.getState()?.app?.accessToken;
  }

  private dispatchConnected() {
    if (this.connectedDispatched || !this.store) {
      return;
    }
    this.connectedDispatched = true;
    this.store.dispatch({
      type: StreamApiActions.CONNECTED,
      payload: { status: 'OK' },
    });
  }

  /**
   * Only fires when the store believes the stream is up. A reconnect loop that
   * fails ten times must not notify every subscriber ten times over.
   */
  private dispatchClosed(reason: string) {
    if (!this.connectedDispatched || !this.store) {
      return;
    }
    this.connectedDispatched = false;
    this.store.dispatch({
      type: StreamApiActions.CLOSED,
      reason,
    });
  }

  private invalidateGeneration() {
    this.generation += 1;
    this.clearReconnectTimer();
    this.clearFlushTimer();
    this.clearRestoreTimer();
    this.clearRestoreQueue();
    this.inbound.cancel();
  }

  private dropClient() {
    const old = this.client;
    this.client = null;
    this.nullLiveRefs();
    // A client that is already closing rejects; nothing here can act on it.
    old?.deactivate().catch(() => {});
  }

  private nullLiveRefs() {
    this.registry.forEach(entry => {
      entry.ref = null;
    });
  }

  private bindEnvironmentListeners() {
    if (
      this.listenersBound ||
      !this.shouldBindEnvironmentListeners ||
      typeof window === 'undefined' ||
      typeof window.addEventListener !== 'function'
    ) {
      return;
    }
    this.listenersBound = true;
    window.addEventListener('online', this.handleEnvironmentWake);
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', this.handleEnvironmentWake);
    }
  }

  private unbindEnvironmentListeners() {
    if (!this.listenersBound) {
      return;
    }
    this.listenersBound = false;
    window.removeEventListener('online', this.handleEnvironmentWake);
    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener(
        'visibilitychange',
        this.handleEnvironmentWake
      );
    }
  }

  /**
   * Regaining the network or coming back to the tab is far better evidence that
   * a retry will succeed than the backoff timer, so collapse the wait. A hidden
   * tab keeps waiting out its backoff instead: it will wake on becoming visible.
   */
  private handleEnvironmentWake = () => {
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    if (
      this.intentionalDisconnect ||
      !this.accessToken ||
      this.reconnectTimer == null
    ) {
      return;
    }
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.startClient();
  };

  private clearReconnectTimer() {
    if (this.reconnectTimer != null) {
      this.clock.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearFlushTimer() {
    if (this.flushTimer != null) {
      this.clock.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private clearRestoreTimer() {
    if (this.restoreTimer != null) {
      this.clock.clearTimeout(this.restoreTimer);
      this.restoreTimer = null;
    }
  }

  private setState(state: ConnectionState) {
    if (this.state === state) {
      return;
    }
    const previous = this.state;
    const now = this.clock.now();
    const durationMs = now - this.stateChangedAt;
    this.state = state;
    this.stateChangedAt = now;
    getStompMetricsSink().onConnectionState?.({
      state,
      previous,
      durationMs,
      generation: this.generation,
    });
  }

  /**
   * Bulk drops (a queue clear, an overflow storm) report one metric each but
   * log once for the whole set: `console.warn` is forwarded to Faro, so fifty
   * lines on the logout path would be fifty uploads saying the same thing.
   */
  private recordDrops(drops: OutboundDrop[], categoryFallback: string) {
    if (!drops.length) {
      return;
    }
    let bytes = 0;
    for (let i = 0; i < drops.length; i++) {
      getStompMetricsSink().onDropped?.({
        reason: drops[i].reason,
        category: drops[i].message.command || categoryFallback,
        bytes: drops[i].message.bytes,
      });
      bytes += drops[i].message.bytes;
    }
    if (drops.length === 1) {
      this.warnDropped(
        drops[0].reason,
        drops[0].message.command || categoryFallback,
        bytes
      );
      return;
    }
    console.warn('[stomp] dropped messages', {
      count: drops.length,
      reasons: Array.from(new Set(drops.map(drop => drop.reason))).join(),
      bytes,
    });
  }

  private recordDuplicateSubscription(key: string, channel: string) {
    if (!hasStompMetricsSink()) {
      return;
    }
    getStompMetricsSink().onDuplicateSubscriptionPrevented?.({ key, channel });
  }

  private recordDrop(reason: DropReason, category: string, bytes?: number) {
    getStompMetricsSink().onDropped?.({ reason, category, bytes });
    this.warnDropped(reason, category, bytes);
  }

  /**
   * Kept in production: `console.warn` is the path into Faro, and with no
   * metrics sink installed it is the only signal that anything was dropped.
   */
  private warnDropped(reason: DropReason, category: string, bytes?: number) {
    console.warn('[stomp] dropped message', { reason, category, bytes });
  }

  private emitQueueSize() {
    if (!hasStompMetricsSink()) {
      return;
    }
    getStompMetricsSink().onQueueSize?.(this.outbound.snapshot());
  }

  private emitLogicalCount() {
    if (!hasStompMetricsSink()) {
      return;
    }
    getStompMetricsSink().onLogicalSubscriptions?.({
      topics: this.registry.size,
      refs: this.totalRefs,
    });
  }
}

let singleton: StompConnectionManager | null = null;

export function getStompConnectionManager() {
  if (!singleton) {
    singleton = new StompConnectionManager();
  }
  return singleton;
}

export function resetStompConnectionManagerForTests(
  next?: StompConnectionManager | null
) {
  const previous = singleton;
  singleton = next === undefined ? null : next;
  if (previous && previous !== singleton) {
    previous.disconnect();
  }
}
